import { objectKeys } from "app/helpers/objects";
import type { CategoryDropdownOption } from "app/pages/sales-intelligence/sub-modules/signals/components/filters/SignalNewsFilter/types/CategoryDropdownOption";
import type { NewsCategoriesByType } from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsCategoriesByType";
import type { NewsCategory } from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsCategory";
import type {
    NewsCategoryOption,
    NewsCategoryOptionsByType,
} from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsCategoryOptionsByType";
import type { NewsCategoryType } from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsCategoryType";

export const isCategoryOfType = (type: NewsCategoryType | NewsCategory) => {
    return (entry: CategoryDropdownOption) => entry.type === type;
};

export const isParentCategory = (entry: CategoryDropdownOption) => {
    return !entry.isChild;
};

const getParentCategoryOfType = (type: NewsCategoryType) => {
    return (entry: CategoryDropdownOption) => isCategoryOfType(type)(entry) && isParentCategory(entry);
};

export const mapNewsCategoriesToOptionsWithType = (
    groupedCategoriesByType: NewsCategoriesByType,
): NewsCategoryOptionsByType => {
    let categoriesWithType = {} as NewsCategoryOptionsByType;
    const categoryTypes = objectKeys(groupedCategoriesByType);

    for (const categoryType of categoryTypes) {
        const categoryItems = groupedCategoriesByType[categoryType].map((category) => ({
            id: category,
            type: categoryType,
        }));

        categoriesWithType = {
            ...categoriesWithType,
            [categoryType]: categoryItems,
        };
    }

    return categoriesWithType;
};

export const buildSelectedItemsFromFilter = (
    selectedValues: readonly NewsCategory[],
    options: NewsCategoryOptionsByType,
): readonly (NewsCategory | NewsCategoryType)[] => {
    const categoryTypes = objectKeys(options);

    const selectedCategories = categoryTypes.filter((categoryType) => {
        return options[categoryType].every(({ id: category }) => selectedValues.includes(category));
    });

    return [...selectedValues, ...selectedCategories];
};

export const buildItemsFromOptions = (options: NewsCategoryOptionsByType): readonly CategoryDropdownOption[] => {
    let items: CategoryDropdownOption[] = [];
    const categoryTypes = objectKeys(options);

    for (const categoryType of categoryTypes) {
        const hasParentCategoryItem = items.some(getParentCategoryOfType(categoryType));

        if (!hasParentCategoryItem) {
            const categoryTypeItem: CategoryDropdownOption = {
                id: categoryType,
                type: categoryType,
                isChild: false,
            };

            items = [...items, categoryTypeItem];
        }

        const categoryItems = options[categoryType].map((category: Required<NewsCategoryOption>) => ({
            ...category,
            isChild: true,
        }));

        items = items.concat(categoryItems);
    }

    return items;
};
